import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { compareAsc, differenceInDays, isWithinInterval } from 'date-fns';
import _ from 'lodash';

import { dayStatus } from '../../constants';
import {
    setCalendarAlert,
    setCheckInDay,
    setCheckOutDay,
    setTempCheckOutDay,
} from 'features/userSlice';
import { isNotAvailableDay } from './helpers';
import { useGetBookingData, useGetCalendarDay } from 'hooks';
import { useIsAfterNotAvailableDay } from './hooks';
import './Day.css';

export const Day = ({
    day,
    dayOfMonth,
    mobile,
    nextDay,
    onDatesClick,
    datesOptions,
    setCalendarOpen,
    variant = 'default',
}) => {
    const dispatch = useDispatch();

    const [canMakeDataRequest, setCanMakeDataRequest] = useState(false);
    let userCheckInDay = useSelector((state) => state.user?.checkInDay);

    if (onDatesClick) {
        userCheckInDay = useGetCalendarDay(datesOptions?.checkin);
    }
    let userCheckOutDay = useSelector((state) => state.user?.checkOutDay);
    if (onDatesClick) {
        userCheckOutDay = useGetCalendarDay(datesOptions?.checkout);
    }
    const tempCheckOutDay = useSelector((state) => state.user?.tempCheckOutDay);

    const userCheckInDate = userCheckInDay?.date;
    const userCheckOutDate = userCheckOutDay?.date;
    const tempCheckOutDate = tempCheckOutDay?.date || userCheckInDate;

    const Lang = useSelector((state) => state.miniBookingWidget?.data?.Lang);

    const { getBookingData } = useGetBookingData();

    const selectedPropertyId = useSelector(
        (state) => state.multiproperty?.selectedPropertyId
    );

    const isSelectedDay =
        userCheckInDate === day?.date ||
        userCheckOutDate === day?.date ||
        tempCheckOutDate === day?.date;

    const isDayWithinInterval =
        isWithinInterval(new Date(day?.date), {
            start: new Date(userCheckInDate),
            end: new Date(userCheckOutDate),
        }) ||
        isWithinInterval(new Date(day?.date), {
            start: new Date(userCheckInDate),
            end: new Date(tempCheckOutDate),
        });
    const isDayNotAvailable = isNotAvailableDay(day);
    const isSelecting =
        userCheckInDay?.date === userCheckOutDay?.date || tempCheckOutDay;
    const [
        isAfterNotAvailableDay,
        allowHover,
        notAvailableDayBetweenCheckInAndCurrentDay,
    ] = useIsAfterNotAvailableDay(
        day,
        userCheckInDay,
        tempCheckOutDay || userCheckOutDay
    );

    const status = day?.status;
    let statusClass;

    switch (status) {
        case dayStatus.AVAILABLE:
            statusClass = 'iHotelWebAppCalendarSelectableDay ab-day-available';
            break;
        case dayStatus.SOLD_OUT:
            statusClass = 'ab-day-sold-out';
            break;
        case dayStatus.MIN_STAY:
            statusClass = 'ab-day-min-stay';
            break;
        case dayStatus.NOT_AVAILABLE:
            statusClass = 'ab-day-not-available';
            break;
        case dayStatus.STOP_SALE:
            statusClass = 'ab-day-stop-sale';
            break;
        case dayStatus.CALL_US:
            statusClass = 'ab-day-available';
            break;
        case dayStatus.CLICKABLE:
            statusClass = 'ab-day-clickable';
            break;
        default:
            break;
    }

    if (day?.isLowest) {
        statusClass = 'ab-day-min-price';
    }

    // day is within interval, highlight it
    statusClass += isDayWithinInterval ? ' ab-day-highlighted' : '';
    statusClass +=
        !day?.isEnabled || (isAfterNotAvailableDay && isSelecting)
            ? ' ab-day-disabled'
            : '';

    const handleMouseOver = (day) => {
        const userCheckInDateObj = new Date(userCheckInDate);
        const currentDayObj = new Date(day?.date);

        // current day is before check in day return and also
        // set tempCheckOutDay to null in order to clear highlighted days
        if (compareAsc(currentDayObj, userCheckInDateObj) === -1) {
            if (tempCheckOutDay) {
                if (onDatesClick) {
                    onDatesClick('tmpcheckout', { day: null });
                } else {
                    dispatch(setTempCheckOutDay({ day: null }));
                }
            }
            return;
        }

        if (
            !allowHover &&
            !_.isEmpty(notAvailableDayBetweenCheckInAndCurrentDay) &&
            isSelecting
        ) {
            if (onDatesClick) {
                onDatesClick('tmpcheckout', {
                    day: notAvailableDayBetweenCheckInAndCurrentDay,
                });
            } else {
                dispatch(
                    setTempCheckOutDay({
                        day: notAvailableDayBetweenCheckInAndCurrentDay,
                    })
                );
            }

            return;
        }

        if (userCheckInDay && isSelecting && allowHover) {
            if (onDatesClick) {
                onDatesClick('tmpcheckout', {
                    day: day,
                });
            } else {
                dispatch(setTempCheckOutDay({ day: day }));
            }
        }
    };

    const handleClick = (day, nextDay) => {
        // Order is important, do not change
        if (isAfterNotAvailableDay && isSelecting) {
            return;
        }

        if (isSelecting) {
            // Allow all days to be selected as checkout day, even those that are not
            // available like the ones below.
        } else if (isDayNotAvailable) {
            return;
        }

        const dateObj = new Date(day?.date);
        let userCheckInDateObj = new Date(userCheckInDate);
        let nightsStaying = 0;
        let checkNextDay = true;

        // Do not allow clicking previous not available day
        if (
            differenceInDays(dateObj, userCheckInDateObj) < 0 &&
            isDayNotAvailable
        ) {
            return;
        } else if (differenceInDays(dateObj, userCheckInDateObj) < 0) {
            userCheckInDay = day;
            if (onDatesClick) {
                onDatesClick('checkin', day.date);
            } else {
                dispatch(setCheckInDay({ day: day }));
            }
        } else if (differenceInDays(dateObj, userCheckInDateObj) === 0) {
            // Just check the next day nothing more
        } else {
            if (userCheckInDate === userCheckOutDate) {
                userCheckOutDay = day;
                checkNextDay = false;
                if (onDatesClick) {
                    onDatesClick('checkout', day.date);
                } else {
                    dispatch(setCheckOutDay({ day: day }));
                }
            } else {
                userCheckInDay = day;
                if (onDatesClick) {
                    onDatesClick('checkin', day.date);
                } else {
                    dispatch(setCheckInDay({ day: day }));
                }
            }
        }

        if (checkNextDay) {
            if (
                day?.min_stay <= 1 &&
                (nextDay?.status === dayStatus.SOLD_OUT ||
                    nextDay?.status === dayStatus.NOT_AVAILABLE ||
                    nextDay?.status === dayStatus.STOP_SALE)
            ) {
                userCheckOutDay = nextDay;
                if (onDatesClick) {
                    onDatesClick('checkout', nextDay.date);
                } else {
                    dispatch(setCheckOutDay({ day: nextDay }));
                }
            } else {
                userCheckOutDay = day;
                if (onDatesClick) {
                    onDatesClick('checkout', day.date);
                } else {
                    dispatch(setCheckOutDay({ day: day }));
                }
            }
        }

        // Calculate staying nights and check whether min stay message should be
        // displayed

        userCheckInDateObj = new Date(userCheckInDay?.date);
        const userCheckOutDateObj = new Date(userCheckOutDay?.date);
        nightsStaying = differenceInDays(
            userCheckOutDateObj,
            userCheckInDateObj
        );

        const minStay = userCheckInDay?.min_stay;

        if (minStay > 1 && nightsStaying < minStay) {
            const msg =
                minStay > 1
                    ? `${Lang?.minStayMsg} ${minStay} ${Lang?.nights}`
                    : `${Lang?.minStayMsg} ${minStay} ${Lang?.night}`;

            dispatch(setCalendarAlert({ msg: msg }));
            // Set check out day equal to check in day in order to allow the user to
            // select another day
            if (onDatesClick) {
                onDatesClick('checkout', userCheckInDay.date);
            } else {
                dispatch(setCheckOutDay({ day: userCheckInDay }));
            }
        } else if (nightsStaying > 20) {
            dispatch(
                setCalendarAlert({ msg: Lang?.maxSearchNightsAllowedMsg })
            );
            // Set check out day equal to check in day in order to allow the user to
            // select another day
            if (onDatesClick) {
                onDatesClick('checkout', userCheckInDay.date);
            } else {
                dispatch(setCheckOutDay({ day: userCheckInDay }));
            }
        } else {
            // Set to '' so that calendar message is not displayed
            dispatch(setCalendarAlert({ msg: '' }));
            setCanMakeDataRequest(true);
        }
    };

    useEffect(() => {
        if (
            userCheckInDay?.date !== userCheckOutDay?.date &&
            canMakeDataRequest &&
            !mobile
        ) {
            if (onDatesClick) {
                setCalendarOpen(false);
            } else {
                getBookingData({ propertyId: selectedPropertyId });
            }
        }

        // Set always to false regardless if we make a request or not
        setCanMakeDataRequest(false);
    }, [canMakeDataRequest]);

    if (day === '') {
        return <td className="outofmonth"></td>;
    }

    return (
        <td
            title=""
            onMouseOver={() => handleMouseOver(day)}
            onClick={() => handleClick(day, nextDay)}
            className={`iHotelWebAppCalendarDay ${statusClass} ${
                isSelectedDay ? 'iHotelWebAppCalendarSelectedDate' : ''
            }`}
        >
            <center>
                <div className="ihotelwebappcalendarround dp-cell ">
                    <span className="the-date"> {dayOfMonth} </span>
                    <div className="iHotelWebAppCalendarPriceLabel final-price">
                        {day?.price && (
                            <>
                                {variant === 'symbolFirst' ? (
                                    <>
                                        <span className="eurosign2">
                                            {day?.currency_symbol}
                                            &nbsp;
                                        </span>
                                        <span className="eurosign2">
                                            {day?.price}
                                        </span>{' '}
                                    </>
                                ) : (
                                    <>
                                        <span className="eurosign2">
                                            {day?.price}
                                        </span>{' '}
                                        <span className="eurosign2">
                                            {day?.currency_symbol}
                                            &nbsp;
                                        </span>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </center>
        </td>
    );
};

Day.propTypes = {
    day: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            date: PropTypes.string,
            status: PropTypes.string,
            price: PropTypes.number,
            currency: PropTypes.string,
        }),
    ]),
    dayOfMonth: PropTypes.string,
    mobile: PropTypes.bool,
    nextDay: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            date: PropTypes.string,
            status: PropTypes.string,
            price: PropTypes.number,
            currency: PropTypes.string,
        }),
    ]),
    onDatesClick: PropTypes.func,
    setCalendarOpen: PropTypes.func,
    datesOptions: PropTypes.object,
    variant: PropTypes.oneOf(['default', 'symbolFirst']),
};
